import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { FloppyDiskIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useUpdateOrderItemsAttributesMutation } from '@/app/services/orderItems'
import { selectOrder, useUpdateOrderMutation } from '@/app/services/orders'
import { addItemToUpdateQueue, clearUpdateQueue, selectSelectedItems } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { TbxTooltip } from '@/common/components'
import TbxDialog from '@/common/components/TbxDialog/TbxDialog'
import TbxShadowScroll from '@/common/components/TbxShadowScroll'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { ORDER_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

import SecondaryProcessesBulkEdit from './SecondaryProcessesBulkEdit'
import SettingsBulkEdit from './SettingsBulkEdit'

const OrderItemsBulkUpdate = ({ onClose, open }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { showMutationError } = useMutationErrorSnackbar()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const selectedItemsArray = useSelector(selectSelectedItems)
    const selectedOrder = useSelector((state) => selectOrder(state, { organisationId, orderId }))

    const [updateOrderItems] = useUpdateOrderItemsAttributesMutation()
    const [updateOrder] = useUpdateOrderMutation()

    const [itemQuantity, setItemQuantity] = useState('')
    const [itemCuttingTechnology, setItemCuttingTechnology] = useState('')
    const [itemMaterial, setItemMaterial] = useState('')
    const [itemThickness, setItemThickness] = useState('')
    const [itemWebThickness, setItemWebThickness] = useState('')
    const [itemGrainDirection, setItemGrainDirection] = useState('none')
    const [itemUnit, setItemUnit] = useState('')

    const [itemSecondaryProcesses, setItemSecondaryProcesses] = useState([])
    const [itemShowFolding, setItemShowFolding] = useState(null)
    const [itemSource, setItemSource] = useState('')
    const [itemMaxLength, setItemMaxLength] = useState(null)
    const [itemNumberOfFolds, setItemNumberOfFolds] = useState(null)

    const [isSavingData, setIsSavingData] = useState(false)
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
    const [userMadeSettingsChanges, setUserMadeSettingsChanges] = useState(false)
    const [userMadeFoldingChanges, setUserMadeFoldingChanges] = useState(false)
    const [userMadeSecProcessesChanges, setUserMadeSecProcessesChanges] = useState(false)

    const itemsCount = useMemo(() => selectedItemsArray?.length, [selectedItemsArray])

    useEffect(() => {
        resetState()
    }, [open])

    const resetState = () => {
        setItemQuantity('')
        setItemCuttingTechnology('')
        setItemMaterial('')
        setItemThickness('')
        setItemSecondaryProcesses([])
        setItemSource('')
        setItemMaxLength(null)
        setItemNumberOfFolds(null)
        setItemUnit('')
        setItemGrainDirection('')
    }

    const handleCloseClick = () => {
        setIsConfirmationDialogOpen(true)
    }

    const hideConfirmationDialog = () => {
        setIsConfirmationDialogOpen(false)
    }

    const handleConfirmationDialogClose = () => {
        onClose()
        resetState()
        setIsConfirmationDialogOpen(false)
    }

    const handleSaveChangesClick = async () => {
        await saveChanges()
        onClose()
        resetState()
        setIsConfirmationDialogOpen(false)
    }

    const saveChanges = async () => {
        setIsSavingData(true)
        selectedItemsArray.forEach((itemId) => dispatch(addItemToUpdateQueue(itemId)))
        const orderItems = {
            quoteItemIds: selectedItemsArray,
            attributes: {
                ...(Boolean(itemQuantity) && { quantity: itemQuantity }),
                ...(userMadeSettingsChanges && {
                    ...(itemCuttingTechnology && { cuttingTechnologyId: itemCuttingTechnology }),
                    ...(itemMaterial && { materialId: itemMaterial }),
                    ...(itemThickness && { thickness: itemThickness }),
                    ...(itemThickness && itemWebThickness !== '' && { webThickness: itemWebThickness }),
                    ...(itemGrainDirection && { grainDirection: itemGrainDirection }),
                    ...(itemUnit && { isImperial: itemUnit === 'in' }),
                    ...(itemUnit && { drawingMetadata: { isImperial: itemUnit === 'in' } }),
                }),
                ...(userMadeFoldingChanges && {
                    foldingSource: itemSource,
                    ...(itemShowFolding &&
                        itemNumberOfFolds && { numberOfFolds: itemSource === 'Drawing' ? 0 : itemNumberOfFolds }),
                    ...(itemShowFolding &&
                        itemMaxLength && { maxFoldLength: itemSource === 'Drawing' ? 0 : itemMaxLength }),
                    ...(!itemShowFolding && { numberOfFolds: 0 }),
                    ...(!itemShowFolding && { maxFoldLength: 0 }),
                }),
                ...(userMadeSecProcessesChanges && {
                    secondaryProcess: itemSecondaryProcesses.filter((process) => process.isEnabled !== null),
                }),
            },
        }

        try {
            await updateOrderItems({ organisationId, orderId, orderItems }).unwrap()
            enqueueSnackbar(t('{{length}} parts updated', { length: selectedItemsArray.length }), {
                variant: 'success',
            })
            handleUpdateOrder()
        } catch (error) {
            showMutationError(
                error,
                t('Failed to update {{length}} parts from order', {
                    length: selectedItemsArray.length,
                })
            )
        } finally {
            dispatch(clearUpdateQueue())
            setIsSavingData(false)
        }
    }

    const handleUpdateOrder = async () => {
        try {
            await updateOrder({
                organisationId,
                orderId: orderId,
                order: {
                    ...selectedOrder,
                    status: ORDER_STATUS_ARRAY.NotCalculated,
                },
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('Failed to calculate order'))
        }
    }

    return (
        <>
            <Dialog
                maxWidth="md"
                open={open}
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== 'backdropClick') onClose()
                }}
            >
                <DialogTitle>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="strong1">
                            {t(`Editing ${itemsCount} ${itemsCount > 1 ? 'parts' : 'part'}`)}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                            }}
                        >
                            <TbxTooltip title={t('Save and close')}>
                                <span>
                                    <IconButton
                                        aria-label="save"
                                        color="secondary"
                                        disabled={isSavingData}
                                        onClick={handleSaveChangesClick}
                                    >
                                        <HugeiconsIcon icon={FloppyDiskIcon} />
                                    </IconButton>
                                </span>
                            </TbxTooltip>
                            <TbxTooltip title={t('Close')}>
                                <span>
                                    <IconButton
                                        aria-label="close"
                                        color="secondary"
                                        onClick={handleCloseClick}
                                    >
                                        <HugeiconsIcon icon={Cancel01Icon} />
                                    </IconButton>
                                </span>
                            </TbxTooltip>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 3,
                            flex: 0,
                        }}
                    >
                        <SettingsBulkEdit
                            itemCuttingTechnology={itemCuttingTechnology}
                            itemGrainDirection={itemGrainDirection}
                            itemMaterial={itemMaterial}
                            itemQuantity={itemQuantity}
                            itemThickness={itemThickness}
                            itemUnit={itemUnit}
                            itemWebThickness={itemWebThickness}
                            setItemCuttingTechnology={setItemCuttingTechnology}
                            setItemGrainDirection={setItemGrainDirection}
                            setItemMaterial={setItemMaterial}
                            setItemQuantity={setItemQuantity}
                            setItemThickness={setItemThickness}
                            setItemUnit={setItemUnit}
                            setItemWebThickness={setItemWebThickness}
                            setUserMadeSettingsChanges={setUserMadeSettingsChanges}
                        />
                        <Divider
                            orientation="vertical"
                            flexItem
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '1',
                                gap: 3,
                            }}
                        >
                            <TbxShadowScroll wrapperSx={{ height: 1 }}>
                                <SecondaryProcessesBulkEdit
                                    itemMaxLength={itemMaxLength}
                                    itemNumberOfFolds={itemNumberOfFolds}
                                    itemSecondaryProcesses={itemSecondaryProcesses}
                                    itemShowFolding={itemShowFolding}
                                    itemSource={itemSource}
                                    itemUnit={itemUnit}
                                    setItemMaxLength={setItemMaxLength}
                                    setItemNumberOfFolds={setItemNumberOfFolds}
                                    setItemSecondaryProcesses={setItemSecondaryProcesses}
                                    setItemShowFolding={setItemShowFolding}
                                    setItemSource={setItemSource}
                                    setUserMadeFoldingChanges={setUserMadeFoldingChanges}
                                    setUserMadeSecProcessesChanges={setUserMadeSecProcessesChanges}
                                />
                            </TbxShadowScroll>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
            <TbxDialog
                closeButtonText={t('No')}
                confirmButtonText={t('Yes, close')}
                content={t('Your changes will be lost')}
                handleClose={hideConfirmationDialog}
                handleConfirmClose={handleConfirmationDialogClose}
                isOpen={isConfirmationDialogOpen}
                title={t('Do you want to close without saving?')}
                uniqueKey="bulk-edit-confirmation-dialog"
            />
        </>
    )
}

OrderItemsBulkUpdate.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default OrderItemsBulkUpdate
